import { useRef, useState, useEffect, useCallback } from 'react';
import Row from '@sdk/components/ui/spacing/Row';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import gettext from '@sdk/lang/gettext';
import H2 from '@sdk/components/ui/typography/H2';
import Text14 from '@sdk/components/ui/typography/Text14';
import styled from 'styled-components';
import Clickable from '@sdk/components/primitives/Clickable';
import useInputClipboardCopy from '@sdk/utils/hooks/useInputClipboardCopy';
import TextInputBase from '@sdk/components/forms/TextInputBase';
import Button from '@sdk/components/ui/buttons/Button';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import Href from '@sdk/components/ui/Href';
import useSlingApi from '@sdk/api/useSlingApi';
import { PosIntegrations } from '@sdk/integrations/types';
import Spinner from '@sdk/components/ui/Spinner';
import Tooltip, { hideTooltip } from '@sdk/common/tooltip/Tooltip';

import { AnimatedPopup } from '@web/components/AnimatedPopup/AnimatedPopup';

import CopyIcon from '../../../assets/svg/UI/copy.svg';

const TOAST_LEARN_MORE = 'http://support.getsling.com/en/articles/5041180-sling-and-toast';

const CopyContainer = styled(Clickable)`
  display: flex;
  align-items: center;
  position: absolute;
  right: 10px;
  color: ${({ theme }) => theme.colors.iconGray};
`;

const CenteredRow = styled(Row)`
  position: relative;
  align-items: center;
`;

const CenteredButtonsRow = styled(CenteredRow)`
  justify-content: space-between;
`;

const ButtonsSideRow = styled(CenteredRow)`
  flex-grow: 1;
`;

const EqualWidthButton = styled(Button)<{ marginRight?: number }>`
  margin-right: ${({ marginRight }): number => marginRight || 0}px;
  flex: 2;
`;

const connectUrl =
  __STAGING__ || __DEV__
    ? 'https://preprod.eng.toasttab.com/restaurants/admin/integrations/my-integrations'
    : 'https://www.toasttab.com/restaurants/admin/integrations/my-integrations';

type Props = {
  back: () => void;
};

export const ToastConnectStep = ({ back }: Props) => {
  const inputRef = useRef<HTMLInputElement>();
  const tooltipRef = useRef<HTMLDivElement>();
  const { copyToClipboard } = useInputClipboardCopy(inputRef);
  const { integrations } = useSlingApi();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  // useEffect(() => {
  //   showTooltip((tooltipRef?.current as unknown) as Element);
  // });

  const loadCode = useCallback(async () => {
    setCode('');
    setError(false);
    setIsLoading(true);

    try {
      const { data } = await integrations.getConnectCode(PosIntegrations.toast);
      setCode(data.linkToken);
    } catch (e) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConnectClick = (): void => {
    const tab = window.open(connectUrl, '_blank');
    tab.focus();
  };

  const onCopyClick = (): void => {
    hideTooltip(tooltipRef?.current);
    copyToClipboard();
  };

  useEffect(() => {
    loadCode();
  }, [loadCode]);

  return (
    <>
      <Row>
        <H2 themeColor={ThemeColorType.primaryText}>{gettext('Connect your account to Toast')}</H2>
      </Row>
      <Row mTop={26} mBottom={20}>
        <Text14 themeColor={ThemeColorType.gray}>
          {gettext(
            'Head to Toast and paste the partner group ID below under the location you want to connect to Sling.'
          )}
        </Text14>
      </Row>

      {code && !isLoading && (
        <>
          <CenteredRow>
            <TextInputBase height={40} ref={inputRef} readOnly value={code} />
            <CopyContainer
              ref={tooltipRef}
              data-for="copy-tip"
              data-tip={gettext('Copy')}
              data-place="top">
              <AnimatedPopup onClick={onCopyClick} label={gettext('Copied!')}>
                <CopyIcon />
                <Tooltip id="copy-tip" />
              </AnimatedPopup>
            </CopyContainer>
          </CenteredRow>

          <CenteredButtonsRow mTop={30}>
            <ButtonsSideRow>
              <Href
                themeColor={ThemeColorType.primary}
                fontSize={12}
                href={TOAST_LEARN_MORE}
                target="_blank">
                {gettext('Learn more')}
              </Href>
            </ButtonsSideRow>
            <ButtonsSideRow>
              <EqualWidthButton marginRight={20} buttonType={ButtonType.default} onClick={back}>
                {gettext('Back')}
              </EqualWidthButton>
              <EqualWidthButton buttonType={ButtonType.primary} onClick={onConnectClick}>
                {gettext('Connect')}
              </EqualWidthButton>
            </ButtonsSideRow>
          </CenteredButtonsRow>
        </>
      )}

      {isLoading && (
        <CenteredRow>
          <Spinner />
        </CenteredRow>
      )}

      {error && (
        <>
          <Text14 themeColor={ThemeColorType.gray}>
            {gettext('An error occured. Please try again')}
          </Text14>

          <CenteredButtonsRow mTop={30}>
            <ButtonsSideRow>
              <Href
                themeColor={ThemeColorType.primary}
                fontSize={12}
                href={TOAST_LEARN_MORE}
                target="_blank">
                {gettext('Learn more')}
              </Href>
            </ButtonsSideRow>
            <ButtonsSideRow>
              <EqualWidthButton marginRight={20} buttonType={ButtonType.default} onClick={back}>
                {gettext('Back')}
              </EqualWidthButton>
              <EqualWidthButton buttonType={ButtonType.primary} onClick={loadCode}>
                {gettext('Try again')}
              </EqualWidthButton>
            </ButtonsSideRow>
          </CenteredButtonsRow>
        </>
      )}
    </>
  );
};
